import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { FileText, Upload, Eye, Trash2 } from "lucide-react";

export const FicheTechniqueCard = ({ concertId, fiche, onChange, embedded = false }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/concerts/${concertId}/fiche-technique`, fd);
      onChange(data); toast.success("Fiche technique enregistrée");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const view = async () => {
    const win = window.open("", "_blank");
    try {
      const { data } = await api.get(`/concerts/${concertId}/fiche-technique`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (win) win.location.href = url; else window.location.href = url;
    } catch (err) { if (win) win.close(); toast.error("Impossible d'ouvrir la fiche technique"); }
  };
  const remove = async () => {
    if (!window.confirm("Supprimer la fiche technique ?")) return;
    try { await api.delete(`/concerts/${concertId}/fiche-technique`); onChange(null); toast.success("Fiche technique supprimée"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className={embedded ? "rounded-lg border border-border p-3 flex flex-col" : "rounded-2xl border border-border bg-card p-6 h-full flex flex-col"} data-testid="fiche-technique-card">
      {embedded
        ? <div className="text-sm font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Fiche technique</div>
        : <h2 className="font-display text-lg font-bold flex items-center gap-2"><FileText className="h-5 w-5 text-primary" />Fiche technique</h2>}
      {fiche ? (
        <div className={embedded ? "mt-2" : "mt-4 rounded-lg border border-border p-3"} data-testid="fiche-technique-file">
          <div className="text-sm font-medium break-all">{fiche.filename}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Ajoutée le {new Date(fiche.uploaded_at).toLocaleDateString("fr-FR")}{fiche.uploaded_by ? ` par ${fiche.uploaded_by}` : ""}
          </div>
        </div>
      ) : (
        <p className={`${embedded ? "mt-2" : "mt-4"} text-sm text-muted-foreground`} data-testid="fiche-technique-empty">Aucune fiche technique pour ce spectacle.</p>
      )}
      <div className={`mt-auto ${embedded ? "pt-2" : "pt-4"} flex flex-wrap gap-2`}>
        {fiche && <Button variant="outline" size="sm" onClick={view} data-testid="fiche-technique-view"><Eye className="h-4 w-4 mr-1.5" />Consulter</Button>}
        {isAdmin && (
          <>
            <input ref={inputRef} type="file" className="hidden" accept=".pdf,image/*,.doc,.docx,.xls,.xlsx" onChange={(e) => upload(e.target.files?.[0])} data-testid="fiche-technique-input" />
            <Button size="sm" disabled={busy} onClick={() => inputRef.current?.click()} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="fiche-technique-upload">
              <Upload className="h-4 w-4 mr-1.5" />{busy ? "Envoi..." : fiche ? "Remplacer" : "Uploader"}
            </Button>
            {fiche && <Button variant="ghost" size="sm" onClick={remove} className="text-muted-foreground hover:text-destructive" data-testid="fiche-technique-delete"><Trash2 className="h-4 w-4" /></Button>}
          </>
        )}
      </div>
    </div>
  );
};
